namespace $ {

	export type $shm_hitalama_map_widget_entity_table2_querysnapshot_row = {
		values: any[]
		system: ReturnType<$shm_hitalama_map_widget_entity_table2_record['system_values']>
	}

	/** Optional derived data in the source Land: identical encryption and rights. */
	export class $shm_hitalama_map_widget_entity_table2_querysnapshot extends $giper_baza_entity.with({
		Manifest: $giper_baza_atom_dict,
		Manifest_key: $giper_baza_atom_text,
		Manifests: $giper_baza_dict_to( $giper_baza_atom_dict ),
		Chunks: $giper_baza_dict_to( $giper_baza_atom_dict ),
	}) {
		static head_for( dataset: $shm_hitalama_map_widget_entity_table2_dataset ) {
			return $giper_baza_link.hash_str( `${dataset.link().str}:query-snapshot-v1` ).peer()
		}

		@ $mol_mem
		signature() { return new this.$.$shm_hitalama_map_widget_entity_table2_querysnapshot_signature }

		stamp() { return this.signature().read([ this.land(), this.head().str ]) }

		@ $mol_mem_key
		fields_schema( fields: readonly $shm_hitalama_map_widget_entity_table2_field[] ) {
			const schema = fields.map( field=> [ field.link().str, field.type(), this.signature().subtree([ field.land(), field.head().str ]) ] )
			return schema.some( field=> !field[2] ) ? null : JSON.stringify( schema )
		}

		static json( value: unknown ): string | null {
			return $mol_error_fence( ()=> {
				const text = JSON.stringify( value )
				return text && $mol_compare_deep( value, JSON.parse( text ) ) ? text : null
			}, ()=> null )
		}

		@ $mol_mem_key
		chunk( key: string ) {
			const value = this.Chunks()?.key( key )?.val() ?? null
			return value && $giper_baza_link.hash_str( JSON.stringify( value ) ).str === key ? value : null
		}

		@ $mol_mem
		manifest() {
			const pointer = this.Manifest_key()
			if( !pointer ) return this.Manifest()?.val() ?? null
			// Presence of the new pointer is authoritative. Incomplete delivery
			// must not fall back to an older inline manifest.
			const key = pointer.val()
			if( !key || !$giper_baza_link.check( key ) ) return null
			const value = this.Manifests()?.key( key )?.val() ?? null
			return value && $giper_baza_link.hash_str( JSON.stringify( value ) ).str === key ? value : null
		}

		@ $mol_mem_key
		column_rows( key: readonly [ readonly $shm_hitalama_map_widget_entity_table2_field[], readonly number[] ] ) {
			const [ fields, columns ] = key
			const stamp = this.stamp(), manifest = this.manifest()
			if( !stamp || !manifest || manifest.format !== 'source-unit-json-v1' || manifest.version !== 1
				|| manifest.stamp !== stamp || manifest.schema !== this.fields_schema( fields ) ) return null
			if( columns.some( col=> !Number.isSafeInteger( col ) || col < 0 || col >= fields.length || fields[col].type() !== 'select' ) ) return null
			const result = new Map<string, any[]>()
			let membership: Set<string> | null = null
			for( const col of columns ) {
				const column_keys = manifest.columns
				if( !column_keys || typeof column_keys !== 'object' || Array.isArray( column_keys ) ) return null
				const keys = ( column_keys as Record<string, unknown> )[ String( col ) ]
				if( !Array.isArray( keys ) || keys.length > 1000 || new Set( keys ).size !== keys.length
					|| keys.some( key=> typeof key !== 'string' || !$giper_baza_link.check( key ) ) ) return null
				const seen = new Set<string>()
				for( let from = 0; from < keys.length; from += 32 ) {
					const parts = this.$.$mol_wire_race( ...keys.slice( from, from + 32 ).map( key=> ()=> this.chunk( key ) ) )
					for( const part of parts ) {
						if( !part ) return null
						const entries = Object.entries( part )
						if( entries.length > 128 ) return null
						for( const [ text, value ] of entries ) {
							const link = $giper_baza_link.check( text )
							if( !link || link.land().str !== this.land().link().str || seen.has( text ) ) return null
							seen.add( text )
							const row = result.get( text ) ?? []
							row[ col ] = value
							result.set( text, row )
						}
					}
				}
				if( membership && ( membership.size !== seen.size || [ ...membership ].some( link=> !seen.has( link ) ) ) ) return null
				membership = seen
			}
			return this.stamp() === stamp && manifest.schema === this.fields_schema( fields ) ? result : null
		}

		/** Scalar select projections only; relation labels remain live dependencies. */
		projection_columns( fields: readonly $shm_hitalama_map_widget_entity_table2_field[] ) {
			return fields.flatMap( ( field, col )=> field.type() === 'select' ? [ col ] : [] )
		}

		prepare_columns(
			chunks: readonly Record<string, $shm_hitalama_map_widget_entity_table2_querysnapshot_row>[],
			fields: readonly $shm_hitalama_map_widget_entity_table2_field[],
		) {
			const parts: Record<string, any>[] = []
			const columns: Record<string, string[]> = {}
			for( const col of this.projection_columns( fields ) ) {
				const keys: string[] = [], pending: Record<string, any>[] = []
				let part: Record<string, any> = {}
				let bytes = 2 // JSON object braces; commas add one byte between entries.
				const entries = new Map<string, number>()
				for( const chunk of chunks ) for( const [ link, row ] of Object.entries( chunk ) ) {
					const entry = $mol_charset_encode( JSON.stringify({ [link]: row.values[col] }) ).length - 2
					if( entry + 2 > 48000 ) return null
					const previous = entries.get( link )
					const next_size = entries.size + ( previous === undefined ? 1 : 0 )
					const next_bytes = bytes + entry - ( previous ?? 0 ) + ( previous === undefined && entries.size ? 1 : 0 )
					if( next_size > 128 || next_bytes > 48000 ) {
						pending.push( part ); part = {}; entries.clear(); bytes = 2
					}
					const replaced = entries.get( link )
					bytes += entry - ( replaced ?? 0 ) + ( replaced === undefined && entries.size ? 1 : 0 )
					part[link] = row.values[col]
					entries.set( link, entry )
				}
				if( entries.size ) pending.push( part )
				if( pending.length > 1000 || pending.some( part=> $mol_charset_encode( JSON.stringify( part ) ).length > 48000 ) ) return null
				for( const part of pending ) { parts.push( part ); keys.push( $giper_baza_link.hash_str( JSON.stringify( part ) ).str ) }
				columns[ String( col ) ] = keys
			}
			return { parts, columns }
		}

		@ $mol_mem_key
		rows( fields: readonly $shm_hitalama_map_widget_entity_table2_field[] ) {
			// A recreated source Land may not have loaded its local Units yet.
			// Establish the loading dependency before treating a missing manifest
			// as absence of a snapshot.
			const stamp = this.stamp()
			const manifest = this.manifest()
			if( !manifest || manifest.format !== 'source-unit-json-v1' || manifest.version !== 1 || manifest.schema !== this.fields_schema( fields ) ) return null
			const keys = manifest.keys
			if( !stamp || manifest.stamp !== stamp || !Array.isArray( keys ) || keys.length > 1000 || keys.some( key=> typeof key !== 'string' || !$giper_baza_link.check( key ) ) ) return null
			const result = new Map<string, $shm_hitalama_map_widget_entity_table2_querysnapshot_row>()
			for( let from = 0; from < keys.length; from += 32 ) {
				const chunks = this.$.$mol_wire_race( ...keys.slice( from, from + 32 ).map( key=> ()=> this.chunk( key ) ) )
				for( const chunk of chunks ) {
					if( !chunk ) return null
					for( const [ link, value ] of Object.entries( chunk ) ) {
						if( !value || typeof value !== 'object' ) return null
						const row = value as Partial<$shm_hitalama_map_widget_entity_table2_querysnapshot_row>
						if( !row || !Array.isArray( row.values ) || row.values.length !== fields.length || !row.system || typeof row.system !== 'object' ) return null
						result.set( link, row as $shm_hitalama_map_widget_entity_table2_querysnapshot_row )
					}
				}
			}
			return result
		}

		@ $mol_action
		refresh(
			dataset: $shm_hitalama_map_widget_entity_table2_dataset,
			changed: readonly $shm_hitalama_map_widget_entity_table2_record[],
		) {
			const records = $mol_wire_sync( this ).refresh_records( dataset, changed )
			if( !records ) return false
			const fields = $mol_wire_sync( dataset ).fields()
			return $mol_wire_sync( this ).write( records, fields )
		}

		refresh_records(
			dataset: $shm_hitalama_map_widget_entity_table2_dataset,
			changed: readonly $shm_hitalama_map_widget_entity_table2_record[],
		): $shm_hitalama_map_widget_entity_table2_record[] | null {
			if( !this.can_change() ) return null
			const records = new Map<string, $shm_hitalama_map_widget_entity_table2_record>()
			const previous = this.manifest()
			if( this.Manifest_key() && !previous ) return null
			if( previous ) {
				if( previous.format !== 'source-unit-json-v1' || previous.version !== 1
					|| !Array.isArray( previous.keys ) || previous.keys.length > 1000 ) return null
				// Old parts provide membership only. Never bless their old values with
				// a new source stamp: write() rereads every row in this one source Land.
				for( const key of previous.keys ) {
					if( typeof key !== 'string' || !$giper_baza_link.check( key ) ) return null
					const chunk = this.chunk( key )
					if( !chunk ) return null
					for( const text of Object.keys( chunk ) ) {
						const link = $giper_baza_link.check( text )
						if( !link || link.land().str !== this.land().link().str || link.head().str === this.head().str ) return null
						records.set( text, this.land().Pawn( this.$.$shm_hitalama_map_widget_entity_table2_record ).Head( link.head() ) )
					}
				}
			}
			for( const record of changed ) {
				if( record.land() !== this.land() ) return null
				records.set( record.link().str, record )
			}
			const rows = [ ...records.values() ]
			for( let from = 0; from < rows.length; from += 32 ) {
				// Read local owners concurrently, without scanning other Lands.
				const owners = this.$.$mol_wire_race( ...rows.slice( from, from + 32 ).map( record=> ()=>
					record.Dataset()?.val()?.str ?? record.Initial()?.val()?.['__dataset'],
				) )
				if( owners.some( owner=> owner !== dataset.link().str ) ) return null
			}
			return rows
		}

		read_row( record: $shm_hitalama_map_widget_entity_table2_record, fields: readonly $shm_hitalama_map_widget_entity_table2_field[] ) {
			// Paged relations have dependencies outside this source Land.
			if( fields.some( field=> field.type() === 'list_link' && field.dataset()?.relation_has_pages( record, field ) ) ) return null
			return { values: record.values_for_query( fields ), system: record.system_values() }
		}

		read_rows( records: readonly $shm_hitalama_map_widget_entity_table2_record[], fields: readonly $shm_hitalama_map_widget_entity_table2_field[] ) {
			// Decline unsupported relations before opening any row payload.
			if( records.some( record=> fields.some( field=> field.type() === 'list_link'
				&& field.dataset()?.relation_has_pages( record, field ) ) ) ) return null
			const result: ( $shm_hitalama_map_widget_entity_table2_querysnapshot_row | null )[] = []
			for( let from = 0; from < records.length; from += 32 ) {
				result.push( ...this.$.$mol_wire_race( ...records.slice( from, from + 32 ).map( record=> ()=>
					this.read_row( record, fields ),
				) ) )
			}
			return result
		}

		@ $mol_action
		write( records: readonly $shm_hitalama_map_widget_entity_table2_record[], fields: readonly $shm_hitalama_map_widget_entity_table2_field[] ) {
			if( !this.can_change() ) return false
			if( records.some( record=> record.land() !== this.land() || record.head().str === this.head().str ) ) return false
			if( fields.some( field=> field.land() === this.land() && field.head().str === this.head().str ) ) return false
			// Never overwrite an unrelated pawn if the deterministic head collides.
			if( !this.head().str || this.head().str === $giper_baza_land_root.tine.str ) return false
			if( this.units().length && this.manifest()?.format !== 'source-unit-json-v1' ) return false
			this.land().units_signing()
			for( const land of new Set( fields.map( field=> field.land() ) ) ) land.units_signing()
			const stamp = this.stamp()
			if( !stamp ) return false
			const schema = this.fields_schema( fields )
			if( !schema ) return false
			const current = this.manifest()
			if( current?.version === 1 && current.stamp === stamp && current.schema === schema ) {
				const rows = this.rows( fields )
				const columns = this.projection_columns( fields )
				if( rows && records.every( record=> rows.has( record.link().str ) )
					&& ( !columns.length || this.column_rows([ fields, columns ]) ) ) return true
			}
			const chunks: Record<string, $shm_hitalama_map_widget_entity_table2_querysnapshot_row>[] = []
			let chunk: Record<string, $shm_hitalama_map_widget_entity_table2_querysnapshot_row> = {}
			const rows = $mol_wire_sync( this ).read_rows( records, fields )
			if( !rows ) return false
			for( const [ index, record ] of records.entries() ) {
				const row = rows[index]
				if( !row ) return false
				const text = this.$.$shm_hitalama_map_widget_entity_table2_querysnapshot.json( row )
				if( !text || $mol_charset_encode( text ).length > 48000 ) return false
				const link = record.link().str
				if( $mol_charset_encode( JSON.stringify({ [link]: row }) ).length > 48000 ) return false
				const next = { ...chunk, [link]: row }
				if( Object.keys( next ).length > this.chunk_rows_limit() || $mol_charset_encode( JSON.stringify( next ) ).length > 48000 ) {
					chunks.push( chunk )
					chunk = { [link]: row }
				} else chunk = next
			}
			if( Object.keys( chunk ).length ) chunks.push( chunk )
			if( chunks.length > 1000 ) return false
			if( this.stamp() !== stamp || this.fields_schema( fields ) !== schema ) return false
			// Content-addressed parts cannot be confused with an older snapshot
			// if CRDT delivery is partial or the manifest arrives first.
			const keys = chunks.map( chunk=> $giper_baza_link.hash_str( JSON.stringify( chunk ) ).str )
			const projected = $mol_wire_sync( this ).prepare_columns( chunks, fields )
			if( !projected ) return false
			const manifest = { format: 'source-unit-json-v1', version: 1, stamp, schema, keys, columns: projected.columns }
			if( $mol_charset_encode( JSON.stringify( manifest ) ).length > 48000 ) return false
			const data = this.Chunks( null )!
			const slots = keys.map( key=> data.key( key, null )! )
			for( let index = 0; index < chunks.length; ++index ) slots[index].val( chunks[index] )
			for( const part of projected.parts ) data.key( $giper_baza_link.hash_str( JSON.stringify( part ) ).str, null )!.val( part )
			if( this.stamp() !== stamp || this.fields_schema( fields ) !== schema ) return false
			// IDB reads Unit and Ball separately by mutable path. Reusing a large
			// manifest path can pair an old Unit with a new encrypted Ball. Store
			// immutable content at distinct paths, publish the small pointer last.
			const key = $giper_baza_link.hash_str( JSON.stringify( manifest ) ).str
			this.Manifests( null )!.key( key, null )!.val( manifest )
			this.Manifest_key( null )!.val( key )
			return true
		}

		/** Keep the published layout unchanged; isolated benchmarks may vary it. */
		chunk_rows_limit() { return 32 }
	}
}
